import React from 'react';

/**
 * Defines the structure for a single breadcrumb item.
 */
export interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

/**
 * Props for the Breadcrumbs component.
 */
interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

/**
 * A navigation component that displays a breadcrumb trail.
 * @param {BreadcrumbsProps} props - The props for the component.
 * @returns {React.ReactElement} - The rendered breadcrumbs component.
 */
const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  return (
    <nav aria-label="Breadcrumb" className="mb-6">
      <ol className="flex items-center space-x-2 text-sm">
        {items.map((item, index) => (
          <li key={index} className="flex items-center">
            {/* Add a separator before each item except the first one */}
            {index > 0 && (
              <svg className="h-5 w-5 flex-shrink-0 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            )}
            {/* If an onClick handler is provided, render a button for interactivity */}
            {item.onClick ? (
              <button
                onClick={item.onClick}
                disabled={item.disabled}
                className={`ml-2 font-medium text-gray-600 hover:text-indigo-600 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-sm disabled:text-gray-400 disabled:hover:text-gray-400 disabled:cursor-not-allowed`}
              >
                {item.label}
              </button>
            ) : (
              // Otherwise, render a non-interactive span for the current page
              <span className="ml-2 font-medium text-gray-500" aria-current="page">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
